import { Link } from 'react-router';
import { Package, ShieldAlert, ShieldCheck } from 'lucide-react';
import { ROUTES } from '@/constants';
import { useDashboard } from '@/services/queries';
import { AccentCard } from '@/shared/ui/accent-card';
import { SectionHeading, JumpButton } from '@/shared/ui/section-heading';
import { StatusPill } from '@/shared/ui/status-pill';
import { Skeleton } from '@/shared/ui/skeleton';
import { formatDateTime } from '@/shared/lib/format';
import { stageLabel } from '@/lib/format';

/**
 * What is currently published, and what is moving.
 *
 * The other half of the dashboard: the fleet counters say how the estate is
 * behaving, this says what it is running and what is about to change.
 *
 * ── Code signing gets its own line ──────────────────────────────────────────
 *
 * Rather than being a field in a table. An unsigned installer is refused by
 * Windows on the customer's machine, and this is the last screen anybody looks
 * at before deciding a release went fine. It is also the card's tone: an
 * unsigned build turns the whole thing red, because a release that cannot
 * install is not a release.
 */
export function ReleaseSnapshot() {
  const query = useDashboard(24);
  const latest = query.data?.latestVersion ?? null;
  const deployments = query.data?.recentDeployments ?? [];

  const unsigned = latest !== null && !latest.codeSigned;

  return (
    <section>
      <SectionHeading
        action={
          <JumpButton
            label="All releases"
            render={(content, className) => (
              <Link to={ROUTES.versions} className={className}>
                {content}
              </Link>
            )}
          />
        }
      >
        Releases
      </SectionHeading>

      <div className="grid gap-4 lg:grid-cols-2">
        <AccentCard
          icon={<Package size={16} strokeWidth={2} />}
          tone={unsigned ? 'negative' : 'brand'}
          bar={unsigned ? 'negative' : 'brand'}
          title="Latest stable build"
        >
          {query.isPending && <Skeleton className="mb-4 h-8 w-40" />}

          {!query.isPending && !latest && (
            <p className="mb-4 text-[12.5px] text-muted-foreground">
              Nothing published to the stable channel yet.{' '}
              <Link to={ROUTES.versions} className="text-primary underline underline-offset-4">
                Publish a build
              </Link>
              .
            </p>
          )}

          {latest && (
            <div className="mb-4">
              <div className="mb-2.5 flex flex-wrap items-center gap-2">
                <span className="font-mono text-[28px] leading-none font-bold tracking-[-0.8px] tabular-nums">
                  {latest.version}
                </span>
                <StatusPill status={latest.channel} />
                {latest.mandatory && <StatusPill status="high">Mandatory</StatusPill>}
              </div>

              <p
                className={`flex items-center gap-1.5 text-[12.5px] font-semibold ${
                  latest.codeSigned ? 'text-pill-positive-foreground' : 'text-destructive'
                }`}
              >
                {latest.codeSigned ? (
                  <>
                    <ShieldCheck className="size-4" /> Code signed
                  </>
                ) : (
                  <>
                    <ShieldAlert className="size-4" /> Not code signed — Windows will refuse it
                  </>
                )}
              </p>

              <p className="mt-1.5 text-[11.5px] text-muted-foreground">
                Published {formatDateTime(latest.publishedAt)}
              </p>
            </div>
          )}
        </AccentCard>

        <AccentCard
          icon={<Package size={16} strokeWidth={2} />}
          title={
            <div className="flex items-baseline justify-between gap-2">
              <span>Recent deployments</span>
              <span className="font-mono text-[10.5px] text-muted-foreground">LAST 5</span>
            </div>
          }
        >
          <div className="mb-4">
            {query.isPending &&
              Array.from({ length: 3 }, (_, index) => (
                <Skeleton key={index} className="mb-2 h-6 w-full" />
              ))}

            {!query.isPending && deployments.length === 0 && (
              <p className="text-[12.5px] text-muted-foreground">No deployments recorded.</p>
            )}

            {deployments.map((deployment) => (
              <div
                key={deployment.id}
                className="flex items-center justify-between gap-2 border-b border-border py-2 text-[12.5px] last:border-0"
              >
                <span className="text-muted-foreground">{deployment.environment}</span>
                <StatusPill status={deployment.stage} minWidth={92}>
                  {stageLabel(deployment.stage)}
                </StatusPill>
              </div>
            ))}
          </div>
        </AccentCard>
      </div>
    </section>
  );
}
